/* eslint-disable import/no-anonymous-default-export */

import { useState, useContext, useEffect } from 'react';
import dayjs from 'dayjs';
import { formatListData, AjaxContext, AppContext } from 'commons-ui';
import { SDFilters, defaultSDFilter } from './constants';

const useService = () => {
	const { getData } = useContext(AjaxContext);
	const {
		showAlert,
		app: { appName }
	} = useContext(AppContext);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [pageNumber, setPageNumber] = useState<any>({ currPage: 0, nextPage: null });
	const [downtimeData, setDowntimeData] = useState<any>({ data: [], rowCount: 0 });
	const [filters, setFilters] = useState<SDFilters | undefined>(defaultSDFilter);

	const getDowntimeData = async (cData: any, jumpPageNumber?: number) => {
		try {
			if (!filters) return;
			const nxtPage: number = jumpPageNumber || 0;
			setShowLoader(true);
			const response = await getData({
				endpoint: '/marketing/dashboard/downtime/fetchAllDowntimes',
				queryParams: {
					timestamp: filters?.startTime,
					status: filters?.schedule,
					featureType: filters?.featureType,
					page: nxtPage,
					isActive: filters?.isActive,
					paymentMode: filters?.paymentMode,
					appName
				}
			});
			if (response?.error) {
				showAlert(`Error while getting scheduled downtime data! Error: ${response?.data?.error}`, { type: 'error' });
				return;
			}
			const listData = formatListData(response?.data?.items);
			listData.forEach((data: any, index: number) => {
				if (data?.instruments) {
					listData[index].instrumentType = data?.instruments?.type;
					listData[index].cardType = data?.instruments?.cardType;
					listData[index].network = data?.instruments?.network;
					listData[index].issuer = data?.instruments?.issuer;
				}
			});
			const nextOffset: number | null = response?.data?.items?.length > 0 && response?.data?.page >= 0 ? nxtPage + 1 : null;
			setDowntimeData({ rowCount: response?.data?.items?.length, data: listData });
			setPageNumber({ currPage: nxtPage, nextPage: nextOffset });
			setForceUpdate(true);
		} catch (error: any) {
			setDowntimeData({ data: [], rowCount: 0 });
			setPageNumber({ currPage: 0, nextPage: null });
			setForceUpdate(true);
			showAlert(`Error while getting scheduled downtime data! Error: ${error?.response?.data?.error}`, { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const jumpBack = async () => {
		if (pageNumber.currPage > 0) {
			await getDowntimeData(null, pageNumber.currPage - 1);
		}
	};

	const jumpNext = async () => {
		if (pageNumber.nextPage !== null) {
			await getDowntimeData(null, pageNumber.nextPage);
		}
	};

	const getAllDowntimeData = () => {
		setForceUpdate(false);
		return downtimeData;
	};

	useEffect(() => {
		getDowntimeData(null, 0);
	}, [filters]);

	const handleFormSubmit = async (filterData: any) => {
		try {
			if (!filterData?.startDate || !filterData?.startTime) {
				showAlert('Please select a suitable date and time.', { type: 'error' });
			} else if (!filterData?.schedule) {
				showAlert('Please select a suitable schedule.', { type: 'error' });
			} else if (!filterData?.featureType) {
				showAlert('Please select a suitable feature type.', { type: 'error' });
			} else if (!filterData?.paymentMode) {
				showAlert('Please select a suitable payment mode.', { type: 'error' });
			} else {
				const newFilterData = {
					...filterData,
					startTime: dayjs(`${filterData.startDate} ${filterData.startTime}`).valueOf().toString()
				};
				delete newFilterData?.startDate;
				setFilters(newFilterData);
			}
			return;
		} catch (error) {
			showAlert('Error in fetching invoice data', { type: 'error' });
		}
	};

	return {
		getDowntimeData,
		showLoader,
		setShowLoader,
		forceUpdate,
		jumpBack,
		jumpNext,
		pageNumber,
		getAllDowntimeData,
		setForceUpdate,
		handleFormSubmit,
		filters
	};
};

export default useService;
